/**
 * Camlin Companion — proactive-engagement control-plane client (doc 116
 * §2.4/§7 D3, CO2).
 *
 * Calls schema-service's server-side cap enforcement
 * (`apps/schema-service/src/companion/engagementStore.ts`) so "a client bug
 * can't nag" — the client-side check in `proactive.ts` is the fast local UX
 * gate, this is the control. Both must agree for a nudge to actually show;
 * either one failing closed is enough to suppress it.
 *
 * Shares the same base-URL shape as `controlPlaneClient.ts` (a
 * `controlPlaneBaseUrl` override, defaulting to the not-yet-hosted
 * `companion.camlinconnect.com.au`) — CompanionWidget passes whatever base
 * URL it resolved for the site config fetch so both control-plane calls
 * point at the same place in local dev.
 */

export const COMPANION_ENGAGEMENT_ATTEMPT_PATH = '/generate/companion-engagement/attempt';
export const COMPANION_ENGAGEMENT_DISMISS_PATH = '/generate/companion-engagement/dismiss';

export interface CompanionProactiveCapsResponse {
  enabled: boolean;
  maxNudgesPerPage: number;
  maxNudgesPerSession: number;
  dismissCoolOffDays: number;
}

export interface CompanionEngagementAttemptResult {
  allowed: boolean;
  reason?: 'proactive_disabled' | 'dismiss_cooloff' | 'page_cap' | 'session_cap';
  caps?: CompanionProactiveCapsResponse;
}

export interface CompanionEngagementAttemptParams {
  siteKey: string;
  visitorId: string;
  sessionId: string;
  pagePath: string;
}

export interface CompanionEngagementDismissParams {
  siteKey: string;
  visitorId: string;
}

export interface CompanionEngagementClient {
  /** Fails CLOSED (`allowed: false`) on any network/HTTP error — a control-plane outage must never let proactive nudging run unchecked. */
  attemptNudge(params: CompanionEngagementAttemptParams): Promise<CompanionEngagementAttemptResult>;
  /** Best-effort — never throws. A dropped dismiss call degrades to "the client-side dismiss record still suppresses locally", not to a crash. */
  dismiss(params: CompanionEngagementDismissParams): Promise<void>;
}

export interface CompanionEngagementClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

export function createCompanionEngagementClient(
  options: CompanionEngagementClientOptions
): CompanionEngagementClient {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl.replace(/\/$/, '');

  return {
    async attemptNudge(params) {
      try {
        const response = await fetchImpl(`${baseUrl}${COMPANION_ENGAGEMENT_ATTEMPT_PATH}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params)
        });

        if (!response.ok) {
          return { allowed: false };
        }

        const payload = (await response.json()) as CompanionEngagementAttemptResult;
        return {
          allowed: payload.allowed === true,
          ...(payload.reason ? { reason: payload.reason } : {}),
          ...(payload.caps ? { caps: payload.caps } : {})
        };
      } catch {
        return { allowed: false };
      }
    },

    async dismiss(params) {
      try {
        await fetchImpl(`${baseUrl}${COMPANION_ENGAGEMENT_DISMISS_PATH}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params)
        });
      } catch {
        // Best-effort — the local dismiss record (proactive.ts, localStorage)
        // already suppresses future nudges for this visitor regardless.
      }
    }
  };
}
